import { AccordionTypes, UI, VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import React, { useEffect, useState } from 'react';

import {
  RecommendedPortfolioBadge,
  RecommendedPortfolioBadgeProps,
} from '../../../../../../../components/RecommendedPortfolioBadge';
import { QuoteButton, QuoteButtonProps } from '../../../../QuoteButton';
import { NotFoundTable, ProductFieldProps } from '../../constants';

import { AssetPrincipalCard } from './components/AssetPrincipalCard';
import { AssetPrincipalCardLoading } from './components/AssetPrincipalCardLoading';

export interface ProductTableMobileProps<T> {
  data: T[];
  isLoading: boolean;
  columns: number;
  rows: number;
  fields: ProductFieldProps<T>[];
  secondaryInfo?: React.FC<{ product: T }>;
  quoteButtonInfoGetter?: (item: T) => QuoteButtonProps;
  recommendedPortfolioInfoGetter?: (item: T) => RecommendedPortfolioBadgeProps;
}

export const ProductTableMobile = <T,>({
  data,
  fields,
  isLoading,
  columns,
  rows,
  secondaryInfo: SecondaryInfo,
  quoteButtonInfoGetter,
  recommendedPortfolioInfoGetter,
}: ProductTableMobileProps<T>) => {
  const principalField = fields[0];
  const otherFields = fields.slice(1);
  const hasQuoteButton = !!quoteButtonInfoGetter;
  const hasRecommendedPortfolioBadge = !!recommendedPortfolioInfoGetter;

  const [accordionState, setAccordionState] = useState<AccordionTypes.Status[]>(
    data.map(() => 'closed')
  );

  const handlePress = (index: number) => {
    setAccordionState(
      accordionState.map((status, i) =>
        i === index ? (status === 'closed' ? 'opened' : 'closed') : status
      )
    );
  };

  const handleClosed = (index: number) => {
    setAccordionState(accordionState.map((status, i) => (i === index ? 'closed' : status)));
  };

  const renderQuoteButton = (item: T) => {
    if (!hasQuoteButton) {
      return null;
    }
    const quoteButtonInfo = quoteButtonInfoGetter(item);

    return (
      <QuoteButton {...quoteButtonInfo} customProps={{ p: 0, ...quoteButtonInfo.customProps }} />
    );
  };

  const renderRecommendedPortfolioBadge = (item: T) => {
    if (!hasRecommendedPortfolioBadge) {
      return null;
    }
    const recommendedPortfolioInfo = recommendedPortfolioInfoGetter(item);

    return <RecommendedPortfolioBadge {...recommendedPortfolioInfo} />;
  };

  const renderProductItem = (item: T, index: number) => {
    if (!SecondaryInfo) {
      return (
        <AssetPrincipalCard
          key={index.toString()}
          item={item}
          principalInfo={principalField.key}
          infos={otherFields}
          columns={columns}
          rows={rows}
          customHeaderButton={renderQuoteButton(item)}
          customRecommendedPortfolioBadge={renderRecommendedPortfolioBadge(item)}
        />
      );
    }

    return (
      <UI.Accordion
        testId="invested-assets-filter-accordion"
        status={accordionState[index]}
        onPress={() => handlePress(index)}
        onClosed={() => handleClosed(index)}
        hasShadow={false}>
        <UI.Accordion.Visible hideDefaultIcons shadow="none" containerProps={{ p: 0 }}>
          <AssetPrincipalCard
            key={index.toString()}
            item={item}
            principalInfo={principalField.key}
            infos={otherFields}
            columns={columns}
            rows={rows}
            onPress={() => handlePress(index)}
            customHeaderButton={renderQuoteButton(item)}
          />
        </UI.Accordion.Visible>
        <UI.Accordion.Expanded maxContentHeight="80vh">
          <VStack space={Spacing['stack.xxxs']}>
            <AssetPrincipalCard
              key={index.toString()}
              item={item}
              principalInfo={principalField.key}
              infos={otherFields}
              columns={columns}
              rows={rows}
            />
            <SecondaryInfo product={item} />
          </VStack>
        </UI.Accordion.Expanded>
      </UI.Accordion>
    );
  };

  const renderProductItemLoading = (index: number) => (
    <AssetPrincipalCardLoading
      key={index.toString()}
      columns={columns}
      rows={rows}
      infos={otherFields}
      forceShowButton={!!SecondaryInfo}
    />
  );

  useEffect(() => {
    setAccordionState(data.map(() => 'closed'));
  }, [data]);

  if (isLoading) {
    return (
      <VStack space={Spacing['stack.xxxs']}>
        {Array.from({ length: 3 }).map((_, index) => renderProductItemLoading(index))}
      </VStack>
    );
  }

  if (data?.length === 0 && !isLoading) {
    return <NotFoundTable />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']} pb={Spacing['stack.xxxs']}>
      {data.map((item, index) => renderProductItem(item, index))}
    </VStack>
  );
};
